<template>
  <div class="ShareNote">
    <!-- 加载中 -->
    <div v-if="loading" class="center-container">
      <img src="/icon/loading.svg" class="loading-icon">
      <div class="loading-text">正在加载文档内容...</div>
    </div>
    
    <!-- 内容显示 -->
    <template v-else>
      <div class="share-header">
        <h1>{{ title }}</h1>
      </div>
      <div id="SharePreview"></div>
    </template>
  </div>
</template>

<script>
import Vditor from 'vditor'

export default {
  name: 'ShareNote',
  data () {
    return {
      title: '',
      content: '',
      loading: true,
      error: '',
      vditor: null
    }
  },
  mounted () {
    // 强制清除可能存在的全局 Loading
    this.$Common.RemoveLoading()
    // 移除 index.html 中的静态 Loading
    const initialLoading = document.getElementById('InitialLoading')
    if (initialLoading) initialLoading.remove()

    // 启动一个定时器，在页面加载的前2秒内不断尝试清除 Loading，防止其他组件（如 Vditor）异步触发全局 Loading
    const clearLoadingTimer = setInterval(() => {
      this.$Common.RemoveLoading()
      const initLoad = document.getElementById('InitialLoading')
      if (initLoad) initLoad.remove()
    }, 100)
    setTimeout(() => {
      clearInterval(clearLoadingTimer)
    }, 2000)

    this.GetSharedContent()
    this.GetTheme()
  },
  methods: {
    GetTheme: function () {
      const urlSlug = this.$route.params.urlSlug || this.$route.query.urlSlug
      if (!urlSlug) return

      this.$Common.ApiList(this, 'ThemeShare').then(url => {
        this.axios.get(`${url}/${urlSlug}`).then(res => {
          if (res.data.code === 200 && res.data.data) {
            this.ApplyTheme(res.data.data.themeUrl)
          }
        })
      })
    },
    ApplyTheme: function (url) {
      const styleId = 'custom-markdown-theme-style'
      const linkId = 'custom-markdown-theme'
      
      const oldLink = document.getElementById(linkId)
      if (oldLink) oldLink.remove()

      let style = document.getElementById(styleId)
      
      if (!url) {
        if (style) style.remove()
        return
      }

      // 处理 URL
      let requestUrl = url
      // 如果是腾讯云 COS 的地址，走本地代理以解决 CORS 问题
      const cosHost = 'https://motemd-1382672407.cos.ap-shanghai.myqcloud.com'
      if (requestUrl.startsWith(cosHost)) {
        requestUrl = requestUrl.replace(cosHost, '/cos-proxy')
      }

      // 使用原生 fetch 请求
      fetch(requestUrl)
        .then(res => {
          if (!res.ok) {
            console.error('Theme fetch failed with status:', res.status, res.statusText)
            throw new Error(`Network response was not ok: ${res.status}`)
          }
          return res.text()
        })
        .then(cssContent => {
          if (!style) {
            style = document.createElement('style')
            style.id = styleId
            document.head.appendChild(style)
          }
          
          // 将 body, html, #write 选择器替换为 &，以便在嵌套中指向父级
          cssContent = cssContent.replace(/(^|[\s,}])(body|html|#write)(?=[\s,{])/gi, '$1&')

          // 预处理：如果 CSS 中已经包含了 .vditor-reset，将其移除，避免重复嵌套
          cssContent = cssContent.replace(/\.vditor-reset/g, '')
          
          // 使用 ID 选择器 (#SharePreview) 增加权重，确保覆盖 Vditor 默认样式
          style.textContent = `
            #SharePreview .vditor-reset {
              ${cssContent}
            }
            #SharePreview .vditor-reset {
              max-width: 1200px !important;
              width: 100% !important;
              box-sizing: border-box !important;
              margin: 0 auto !important;
            }
          `
        })
        .catch(err => {
          console.error('Failed to load theme:', err)
          if (style) style.remove()
        })
    },
    GetSharedContent: function () {
      const urlSlug = this.$route.params.urlSlug
      if (!urlSlug) {
        this.error = '无效的文档链接'
        this.loading = false
        return
      }

      this.$Common.ApiList(this, 'GetSharedContent').then(url => {
        // 使用配置的 API 地址
        const requestUrl = `${url}/${urlSlug}`
        console.log('Fetching shared content from:', requestUrl)
        
        this.axios.get(requestUrl).then(res => {
          if (res.data.code === 200) {
            const data = res.data.data
            this.title = data.nodeName || '无标题文档'
            const rawContent = data.content || ''
            this.content = rawContent
            
            this.loading = false
            // 确保 DOM 更新后再渲染
            this.$nextTick(() => {
              this.RenderContent()
            })
          } else {
            this.error = res.data.message || '获取文档失败或文档未开启分享'
            this.loading = false
          }
        }).catch(err => {
          console.error('Share content fetch error:', err)
          this.error = '网络请求失败或服务不可用'
          this.loading = false
        })
      })
    },
    RenderContent: function () {
      // 销毁旧实例（如果有）
      if (this.vditor) {
        this.vditor.destroy()
        this.vditor = null
      }

      this.$nextTick(() => {
        const previewElement = document.getElementById('SharePreview')
        if (!previewElement) return

        this.vditor = new Vditor('SharePreview', {
          value: this.content,
          mode: 'ir',
          height: '100%',
          width: '100%',
          placeholder: '',
          outline: {
            enable: true,
            position: 'left'
          },
          toolbar: [
            'outline',
            'fullscreen',
            'preview',
            'export'
          ],
          cache: { enable: false },
          preview: {
            theme: {
              current: 'light',
              path: '/vditor/dist/css/content-theme'
            }
          },
          after: () => {
            // 设置为只读
            this.vditor.disabled()
            // 默认打开大纲
            this.vditor.outline.toggle(this.vditor, true)
            // 移除全局 Loading
            this.$Common.RemoveLoading()
            // 强制渲染一次，确保内容显示
            this.vditor.setValue(this.content)
          }
        })
      })
    }
  }
}
</script>

<style scoped>
.ShareNote {
  width: 100vw;
  height: 100vh;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: #333;
}

.share-header {
  text-align: center;
  padding: 15px 20px;
  background: #ffffff;
  flex-shrink: 0;
}

.share-header h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #333;
  line-height: 1.4;
}

#SharePreview {
  flex: 1;
  height: 0;
  min-height: 0;
  width: 100%;
  border: unset;
  background: #ffffff;
}

.center-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  margin-top: 20vh;
}

.loading-icon {
  width: 40px;
  animation: rotate 1s linear infinite;
}

.error-icon {
  width: 60px;
  margin-bottom: 20px;
}

.loading-text {
  margin-top: 15px;
  color: #888;
}

.error-text {
  font-size: 18px;
  color: #ccc;
}

/* Typora-like Styles for Preview */
#SharePreview :deep(.vditor) {
  border: none;
  --textarea-background-color: #fff;
  width: 100% !important;
}

#SharePreview :deep(.vditor-content) {
  background: #ffffff;
  position: relative;
  width: 100% !important;
}

#SharePreview :deep(.vditor-outline) {
  position: absolute !important;
  top: 0;
  left: 0;
  height: 100%;
  z-index: 10;
  background: #ffffff;
  border-right: 1px solid #eee;
}

/* Force opacity 1 even when disabled */
:deep(.vditor-content),
:deep(.vditor-ir__preview),
:deep(.vditor-sv__preview),
:deep(.vditor-wysiwyg__preview),
:deep([contenteditable="false"]) {
  opacity: 1 !important;
  color: #333 !important;
  cursor: text !important;
  background-color: #fff !important;
}

/* Fix gray text in IR mode specifically */
:deep(.vditor-ir__node--disabled) {
  opacity: 1 !important;
  color: #333 !important;
}

:deep(.vditor-toolbar) {
  background-color: #fff;
  border-bottom: 1px solid #eee;
  padding-left: 0;
  display: none;
}

:deep(.vditor-content .vditor-reset) {
  font-family: "Open Sans", "Clear Sans", "Helvetica Neue", Helvetica, Arial, sans-serif, "Segoe UI Emoji", "Segoe UI Symbol";
  color: #333;
  line-height: 1.6;
  font-size: 16px;
  padding: 20px 40px !important; /* Force override */
  max-width: 1200px !important; /* Restore max-width but wider */
  width: 100% !important; /* Explicitly set width to 100% */
  box-sizing: border-box !important;
  margin: 0 auto !important; /* Restore centering */
}

:deep(.vditor-reset h1),
:deep(.vditor-reset h2) {
  border-bottom: 1px solid #eaecef;
  padding-bottom: .3em;
  margin-bottom: 16px;
}

:deep(.vditor-reset h1) {
  font-size: 2em;
  font-weight: 600;
}

:deep(.vditor-reset h2) {
  font-size: 1.5em;
  font-weight: 600;
}

:deep(.vditor-reset blockquote) {
  border-left: 4px solid #dfe2e5;
  color: #6a737d;
  background-color: transparent;
  padding: 0 1em;
}

:deep(.vditor-reset a) {
  color: #0366d6;
  text-decoration: none;
}

:deep(.vditor-reset a:hover) {
  text-decoration: underline;
}

:deep(.vditor-reset table) {
  border-collapse: collapse;
  border-spacing: 0;
}

:deep(.vditor-reset table tr) {
  background-color: #fff;
  border-top: 1px solid #c6cbd1;
}

:deep(.vditor-reset table tr:nth-child(2n)) {
  background-color: #f6f8fa;
}

:deep(.vditor-reset table th),
:deep(.vditor-reset table td) {
  padding: 6px 13px;
  border: 1px solid #dfe2e5;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 移动端适配 */
@media (max-width: 768px) {
  .share-header h1 {
    font-size: 18px;
    margin: 0;
  }
  
  #SharePreview :deep(.vditor-content .vditor-reset) {
    padding: 15px !important;
  }
}
</style>